/*
 * <trust-bar> — mobile rotation for sections/trust-bar.liquid.
 *
 * On a phone / tablet (max-width: 989px — the header's mobile split; keep it equal to the media
 * queries in sections/trust-bar.liquid) the bar shows ONE item. Every
 * `data-interval` ms (default 5000) the current item slides out to the left and the next slides in
 * from the right, looping. This file only toggles classes; the motion is CSS keyframes in the
 * section's stylesheet (which also swaps the slide for a short cross-fade under
 * prefers-reduced-motion).
 *
 *   .is-current  shown, static (the first item, before the first rotation)
 *   .is-in       sliding in from the right, then stays shown
 *   .is-out      sliding out to the left; removed on animationend (back to the waiting spot, off-screen)
 *
 * Nothing here touches ARIA: every item stays in the DOM and the accessibility tree and nothing is
 * aria-live, so a screen reader is not interrupted every 5 s.
 *
 * Lifecycle: connectedCallback / disconnectedCallback own the timer and the listeners, so a section
 * re-render in the theme editor (old element removed, new one inserted) neither leaks a timer nor
 * double-rotates. The timer also stops while the tab is hidden and while the viewport is wider than
 * the breakpoint (desktop shows all four items statically, CSS only).
 */
if (!customElements.get('trust-bar')) {
  customElements.define(
    'trust-bar',
    class TrustBar extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.trust-bar__item'));
        this.index = 0;
        this.timer = null;
        this.interval = parseInt(this.dataset.interval, 10) || 5000;
        this.mq = window.matchMedia('(max-width: 989px)');

        this.onMedia = () => {
          this.stop();
          this.reset();
          this.start();
        };
        this.onVisibility = () => (document.hidden ? this.stop() : this.start());

        this.reset();
        this.setAttribute('data-ready', '');

        this.mq.addEventListener('change', this.onMedia);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        if (this.mq) this.mq.removeEventListener('change', this.onMedia);
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      /** Back to the initial state: first item current, the rest waiting off to the right. */
      reset() {
        this.index = 0;
        this.items.forEach((item, i) => {
          item.classList.remove('is-in', 'is-out');
          item.classList.toggle('is-current', i === 0);
        });
      }

      start() {
        if (this.timer || document.hidden || this.items.length < 2 || !this.mq.matches) return;
        this.timer = window.setInterval(() => this.next(), this.interval);
      }

      stop() {
        if (!this.timer) return;
        window.clearInterval(this.timer);
        this.timer = null;
      }

      next() {
        const current = this.items[this.index];
        this.index = (this.index + 1) % this.items.length;
        const upcoming = this.items[this.index];

        // Anything that is neither leaving nor arriving goes back to the waiting spot. This also
        // sweeps a stale .is-out whose animationend never fired (tab throttled, reduced motion).
        this.items.forEach((item) => {
          if (item !== current && item !== upcoming) item.classList.remove('is-current', 'is-in', 'is-out');
        });

        current.classList.remove('is-current', 'is-in');
        current.classList.add('is-out');
        current.addEventListener(
          'animationend',
          (event) => {
            if (event.target === current && event.animationName !== 'trust-bar-in') current.classList.remove('is-out');
          },
          { once: true }
        );

        upcoming.classList.remove('is-current', 'is-out');
        upcoming.classList.add('is-in');
      }
    }
  );
}
